import type { MutableRefObject } from 'react';
import { useSnackbar } from 'notistack';

import type { AppDispatch } from '../../../store';
import { jotdocsApi , useUpdateJotDocMutation } from '../store/api';

import { updateSectionInMarkdown } from './markdown-utils';

interface SaveSectionOptions {
  jotdocId: string;
  originalMarkdown: string;
  sectionId: string;
  newSectionContent: string;
  onSuccess?: (updatedMarkdown: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for saving jotdoc section changes
 * Returns a function that saves a section update to the backend
 */
export function useJotDocSectionSave() {
  const [updateJotDoc, { isLoading: isSaving }] = useUpdateJotDocMutation();
  const { enqueueSnackbar } = useSnackbar();

  const saveSection = async ({
    jotdocId,
    originalMarkdown,
    sectionId,
    newSectionContent,
    onSuccess,
    onError,
  }: SaveSectionOptions): Promise<boolean> => {
    try {
      console.log('[JotDoc] Starting section save', {
        jotdocId,
        sectionId,
        originalMarkdownLength: originalMarkdown.length,
        newSectionContentLength: newSectionContent.length,
      });

      // Reconstruct full markdown with updated section
      const updatedMarkdown = updateSectionInMarkdown(
        originalMarkdown,
        sectionId,
        newSectionContent,
      );

      // Don't save if content hasn't changed
      if (updatedMarkdown === originalMarkdown) {
        console.log('[JotDoc] Save skipped - no changes detected', { sectionId });
        return true;
      }

      console.log('[JotDoc] Calling API to save section', {
        jotdocId,
        sectionId,
        updatedMarkdownLength: updatedMarkdown.length,
      });

      // Save using RTK Query mutation
      const result = await updateJotDoc({
        jotdocId,
        updateJotDocRequest: {
          updated_fields: {
            content: updatedMarkdown,
          },
        },
      }).unwrap();

      console.log('[JotDoc] Section save API call successful', {
        jotdocId,
        sectionId,
        savedContentLength: result?.jotdoc?.content?.length || updatedMarkdown.length,
      });

      // Show success notification
      enqueueSnackbar('Section saved successfully', { variant: 'success' });
      
      // Call onSuccess with the updated markdown (from response or reconstructed)
      const savedMarkdown = result?.jotdoc?.content || updatedMarkdown;
      onSuccess?.(savedMarkdown);
      return true;
    } catch (error: any) {
      const errorMessage =
        error?.data?.message || error?.message || 'Failed to save section';
      console.error('[JotDoc] Section save failed', {
        jotdocId,
        sectionId,
        error: errorMessage,
      });
      enqueueSnackbar(errorMessage, { variant: 'error' });
      onError?.(error instanceof Error ? error : new Error(errorMessage));
      return false;
    }
  };

  const saveMarkdown = async ({
    jotdocId,
    updatedMarkdown,
    onSuccess,
    onError,
  }: {
    jotdocId: string;
    updatedMarkdown: string;
    onSuccess?: (savedMarkdown: string) => void;
    onError?: (error: Error) => void;
  }): Promise<boolean> => {
    try {
      console.log('[JotDoc] Starting markdown save', {
        jotdocId,
        updatedMarkdownLength: updatedMarkdown.length,
      });

      // Save using RTK Query mutation
      const result = await updateJotDoc({
        jotdocId,
        updateJotDocRequest: {
          updated_fields: {
            content: updatedMarkdown,
          },
        },
      }).unwrap();

      console.log('[JotDoc] Markdown save API call successful', {
        jotdocId,
        savedContentLength: result?.jotdoc?.content?.length || updatedMarkdown.length,
      });

      // Show success notification
      enqueueSnackbar('Document saved successfully', { variant: 'success' });
      
      // Call onSuccess with the updated markdown (from response or provided)
      const savedMarkdown = result?.jotdoc?.content || updatedMarkdown;
      onSuccess?.(savedMarkdown);
      return true;
    } catch (error: any) {
      const errorMessage =
        error?.data?.message || error?.message || 'Failed to save document';
      console.error('[JotDoc] Markdown save failed', {
        jotdocId,
        error: errorMessage,
      });
      enqueueSnackbar(errorMessage, { variant: 'error' });
      onError?.(error instanceof Error ? error : new Error(errorMessage));
      return false;
    }
  };

  return {
    saveSection,
    saveMarkdown,
    isSaving,
  };
}

interface UseJotDocSectionSaveWithOptimisticParams {
  jotdocId: MutableRefObject<string | undefined>;
  currentMarkdown: MutableRefObject<string | undefined>;
  sectionContentRef: MutableRefObject<Map<string, string>>;
  dispatch: AppDispatch;
  saveSection: (options: SaveSectionOptions) => Promise<boolean>;
}

/**
 * Hook for saving jotdoc sections with optimistic updates
 * Encapsulates the common pattern of:
 * 1. Updating section content ref
 * 2. Optimistic cache update
 * 3. Backend save with error rollback
 *
 * This hook can be reused across attestation edits, bracket replacement, and future features.
 */
export function useJotDocSectionSaveWithOptimistic({
  jotdocId,
  currentMarkdown,
  sectionContentRef,
  dispatch,
  saveSection,
}: UseJotDocSectionSaveWithOptimisticParams) {
  const saveSectionWithOptimistic = async (
    sectionId: string,
    newContent: string,
  ): Promise<void> => {
    const currentJotdocId = jotdocId.current;
    const currentMarkdownValue = currentMarkdown.current;

    if (!currentJotdocId || !currentMarkdownValue) {
      console.log('[JotDoc] Save with optimistic update ignored - missing jotdoc or markdown', {
        hasJotdoc: !!currentJotdocId,
        hasMarkdown: !!currentMarkdownValue,
      });
      return;
    }

    console.log('[JotDoc] Saving section with optimistic update', {
      sectionId,
      jotdocId: currentJotdocId,
      newContentLength: newContent.length,
    });

    // Update section content ref (local state tracking)
    sectionContentRef.current.set(sectionId, newContent);

    // Optimistically update cache immediately for instant UI feedback
    const patchResult = dispatch(
      jotdocsApi.util.updateQueryData(
        'getV2ProviderJotdocsByJotdocId',
        { jotdocId: currentJotdocId },
        draft => {
          if (draft.jotdoc) {
            // Reconstruct markdown with updated section
            const updatedMarkdown = updateSectionInMarkdown(
              currentMarkdownValue,
              sectionId,
              newContent,
            );
            draft.jotdoc.content = updatedMarkdown;
          }
        },
      ),
    ) as unknown as { undo: () => void };

    // Save the updated section (backend save with error rollback)
    await saveSection({
      jotdocId: currentJotdocId,
      originalMarkdown: currentMarkdownValue,
      sectionId,
      newSectionContent: newContent,
      onError: () => {
        // Rollback optimistic update on error
        patchResult.undo();
      },
    });
  };

  return { saveSectionWithOptimistic };
}

